import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const ON_DUTY_BASE_URL = `${API_BASE_PATH}/on_duty`;

// 📝 POST payload
export interface OnDutyRequest {
  date_apply: string ;
  no_of_days: "Full Day" | "Half Day" | "Custom";
  start_date?: string | null;
  end_date?: string | null;
  session_type?: "Morning" | "Afternoon" | null;
  reason: string;
  notify: string;
}

// 🔁 PUT payload
export interface OnDutyUpdate {
  on_duty_id: number;
  status: "Approved" | "Rejected";
  reject_reason?: string;
  notify: string;
}

// 📥 Response record
export interface OnDutyRecord {
  Od_id: number;
  employee_id: string;
  date_apply: string;
  start_date: string | null;
  end_date: string | null;
  no_of_days: "Full Day" | "Half Day" | "Custom";
  session_type: "Morning" | "Afternoon" | null;
  reason: string;
  status: "Pending" | "Approved" | "Rejected";
  reject_reason: string | null;
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
  updated_at: string | null;
}

// 📊 Widgets response
export interface OnDutyWidgets {
  applied: number;
  approved: number;
  rejected: number;
  pending: number;
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// 🌟 POST - Create an On-Duty request
export const postOnDuty = async (
  data: OnDutyRequest,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    const response = await axios.post(`${ON_DUTY_BASE_URL}/`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("On-Duty request submitted successfully");
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - Fetch own On-Duty records (Employee)
export const getSelfOnDuties = async (
  token: string | null
): Promise<OnDutyRecord[]> => {
  try {
    const response = await axios.get(`${ON_DUTY_BASE_URL}/self/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - Fetch all On-Duty requests (Admin / Manager)
export const getAllOnDuties = async (
  token: string | null
): Promise<OnDutyRecord[]> => {
  try {
    const response = await axios.get(`${ON_DUTY_BASE_URL}/all/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 📊 GET - On-Duty Widgets (Monthly Summary)
export const getOnDutyWidgets = async (
  month: number,
  year: number,
  day?: number | null,
  employee_id?: string | null,
  token?: string | null
): Promise<OnDutyWidgets> => {
  try {
    const params = new URLSearchParams();
    params.append("month", month.toString());
    params.append("year", year.toString());
    if (day) params.append("day", day.toString());
    if (employee_id) params.append("employee_id", employee_id);

    const response = await axios.get(
      `${ON_DUTY_BASE_URL}/widgets?${params.toString()}`,
      {
        headers: {
          accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// ✅ PUT - Approve or Reject an On-Duty request
export const updateOnDutyStatus = async (
  data: OnDutyUpdate,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    formData.append("on_duty_id", data.on_duty_id.toString()); // <-- required in body
    formData.append("status", data.status);
    if (data.reject_reason) {
      formData.append("reject_reason", data.reject_reason);
    }
    formData.append("notify", data.notify);

    const response = await axios.put(
      `${ON_DUTY_BASE_URL}/`, // ❌ no query param, backend wants it in body
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success(`On-Duty ${data.status}`);
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

